import {
  EQUIPMENT_INITIAL_STATE,
  PlayerContextState,
  PlayerContextTransform,
} from "../PlayerContext";
import {
  addToInventory,
  calculateInventoryCap,
  getAmountOfItem,
  getInventory,
  removeFromInventory,
} from "./Inventory";
import { ItemAction, ItemOccurrence } from "./Item";

import bind from "memoize-bind";

export enum EquipmentSlot {
  Hand = "Hand",
  Body = "Body",
  Accessory = "Accessory",
}

export function canUnequipItem(
  state: PlayerContextState,
  slot: EquipmentSlot,
): boolean {
  const item = getEquippedItem(state, slot);
  if (!item) {
    return false;
  }

  if (
    getInventory(state).length >= calculateInventoryCap(state) &&
    getAmountOfItem(item, state) <= 0
  ) {
    return false;
  }

  return true;
}

export function canEquipItem(
  state: PlayerContextState,
  slot: EquipmentSlot,
  item: ItemOccurrence,
): boolean {
  if (getAmountOfItem(item, state) <= 0) {
    return false;
  }

  if (
    getAmountOfItem(item, state) > 1 &&
    getEquippedItem(state, slot) &&
    !canUnequipItem(state, slot)
  ) {
    return false;
  }

  return true;
}

export function equipToSlot(
  state: PlayerContextState,
  slot: EquipmentSlot,
  item: ItemOccurrence,
  skipSave?: boolean,
): PlayerContextState {
  if (!canEquipItem(state, slot, item)) {
    return state;
  }

  state = removeFromInventory(item, 1, state);
  state = unequipFromSlot(slot, skipSave)(state);
  if (!state.equipment) {
    state.equipment = EQUIPMENT_INITIAL_STATE;
  }
  state.equipment[slot] = item;

  if (!skipSave) {
    state = saveEquipmentLoadoutToPosition(
      state,
      state.lastEquipmentLoadoutModified,
    );
  }

  return state;
}

export function getEquippedItem(
  state: PlayerContextState,
  slot: EquipmentSlot,
): ItemOccurrence | undefined {
  return state.equipment?.[slot];
}

function unequipFromSlotImpl(
  slot: EquipmentSlot,
  skipSave: boolean,
  state: PlayerContextState,
): PlayerContextState {
  if (!canUnequipItem(state, slot)) {
    return state;
  }

  const item = getEquippedItem(state, slot);
  if (!item) {
    return state;
  }

  state = addToInventory(item, 1, state);
  if (!state.equipment) {
    state.equipment = EQUIPMENT_INITIAL_STATE;
  }
  state.equipment[slot] = undefined;

  if (!skipSave) {
    state = saveEquipmentLoadoutToPosition(
      state,
      state.lastEquipmentLoadoutModified,
    );
  }

  return state;
}

export function unequipFromSlot(
  this: any,
  slot: EquipmentSlot,
  skipSave?: boolean,
): PlayerContextTransform {
  // Do it this way to avoid creating extra functions and avoid rerenders
  return bind(unequipFromSlotImpl, this, slot, skipSave);
}

export function getUnequipAction(
  state: PlayerContextState,
  slot: EquipmentSlot,
): ItemAction {
  return {
    description: "Unequip",
    isEnabled: canUnequipItem(state, slot),
    transform: unequipFromSlot(slot),
  };
}

export function saveEquipmentLoadoutToPosition(
  state: PlayerContextState,
  position: number,
  name?: string,
): PlayerContextState {
  const equipment = JSON.parse(JSON.stringify(state.equipment));
  state.equipmentLoadouts[position] = {
    name: name ?? "Slot " + (position + 1),
    equipment: equipment,
  };
  state.lastEquipmentLoadoutModified = position;
  return state;
}

export function loadEquipmentLoadoutFromPosition(
  state: PlayerContextState,
  position: number,
): PlayerContextState {
  const equipment = JSON.parse(
    JSON.stringify(
      state.equipmentLoadouts?.[position]?.equipment || {
        Hand: undefined,
        Body: undefined,
        Accessory: undefined,
      },
    ),
  );

  for (let slot of Object.values(EquipmentSlot)) {
    const itemOccurrence = equipment?.[slot];
    const currentItemOccurrence = getEquippedItem(state, slot as EquipmentSlot);
    if (itemOccurrence == null && currentItemOccurrence != null) {
      // We want to unequip since there is nothing assigned to this slot
      if (!canUnequipItem(state, slot as EquipmentSlot)) {
        // Skip as there is no space to unequip
        continue;
      }
      state = unequipFromSlotImpl(slot as EquipmentSlot, true, state);
    } else {
      // We want to equip the new item
      state = equipToSlot(state, slot as EquipmentSlot, itemOccurrence, true);
    }
  }

  state.lastEquipmentLoadoutModified = position;
  return state;
}
